import { useState, useMemo } from 'react';
import { Select, SpaceBetween, Header, Box, Spinner, Button } from '@cloudscape-design/components';
import { useNavigate } from 'react-router-dom';
import { useJourneyGraph } from '../hooks/useJourneyGraph';
import { useConfigs } from '@features/configs';
import { JourneyCanvas } from './JourneyCanvas';
import { JourneyLegend } from './JourneyLegend';
import { NodeInspectPanel } from './panels/NodeInspectPanel';
import { JourneyNodeData } from '../utils/buildGraph';

/**
 * Main Journey View component.
 * Shows DNIS selector, legend, React Flow canvas, and side panel.
 * BR-14: REQ-1, REQ-2, REQ-5
 */
export function JourneyView() {
  const navigate = useNavigate();
  const [selectedDnis, setSelectedDnis] = useState<string | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const { graphData, isLoading, dnisConfigs } = useJourneyGraph(selectedDnis);
  const { data: allConfigsData } = useConfigs();
  const allConfigs = allConfigsData?.items ?? [];

  // Default to first DNIS if none selected
  if (!selectedDnis && dnisConfigs.length > 0) {
    setSelectedDnis(dnisConfigs[0].configId);
  }

  const dnisOptions = dnisConfigs.map((d) => ({
    label: d.configName,
    value: d.configId,
    description: d.configId,
  }));

  // Find the selected node's data for the panel
  const selectedNodeData: JourneyNodeData | null = useMemo(() => {
    if (!selectedNodeId || !graphData) return null;
    const node = graphData.nodes.find((n) => n.id === selectedNodeId);
    return node?.data as JourneyNodeData | null ?? null;
  }, [selectedNodeId, graphData]);

  return (
    <div style={{ position: 'relative' }}>
      <SpaceBetween size="m">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Header variant="h1">IVR Journey View</Header>
          <Button variant="link" onClick={() => navigate('/')}>← Back to List</Button>
        </div>

        {/* DNIS Entry Point Selector */}
        <div style={{ maxWidth: 400 }}>
          <Box variant="small" fontWeight="bold" padding={{ bottom: 'xxs' }}>Entry Point:</Box>
          <Select
            selectedOption={selectedDnis ? dnisOptions.find((o) => o.value === selectedDnis) ?? null : null}
            onChange={({ detail }) => {
              setSelectedDnis(detail.selectedOption?.value ?? null);
              setSelectedNodeId(null);
            }}
            options={dnisOptions}
            placeholder="Select an entry point (DNIS)..."
            filteringType="auto"
            empty="No entry points configured yet. Create a DNIS configuration to see the journey map."
          />
        </div>

        {/* Legend */}
        <JourneyLegend />

        {/* Canvas */}
        {isLoading && (
          <Box textAlign="center" padding={{ top: 'xl' }}>
            <Spinner size="large" />
          </Box>
        )}

        {!isLoading && graphData && graphData.nodes.length > 0 && (
          <JourneyCanvas
            graphData={graphData}
            onNodeClick={(configId) => setSelectedNodeId(configId)}
          />
        )}

        {!isLoading && graphData && graphData.nodes.length === 0 && (
          <Box textAlign="center" padding={{ top: 'xl' }} color="text-status-inactive">
            No journey data found for this entry point.
          </Box>
        )}

        {!isLoading && !selectedDnis && (
          <Box textAlign="center" padding={{ top: 'xl' }} color="text-status-inactive">
            Select a DNIS entry point above to view the call journey.
          </Box>
        )}
      </SpaceBetween>

      {/* Side Panel */}
      {selectedNodeData && (
        <NodeInspectPanel
          nodeData={selectedNodeData}
          allConfigs={allConfigs}
          onClose={() => setSelectedNodeId(null)}
          onNodeNavigate={(configId) => setSelectedNodeId(configId)}
        />
      )}
    </div>
  );
}
